import type { SupportedLocale } from '../../../shared/i18n/locales';
import { getMessagesForLocale, type MessageRegion } from '../i18n/messages';

export interface PageHeadSpec {
  title: string;
  description: string;
  keywords: string;
  author: string;
  canonicalUrl: string;
  siteName: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogImage: string;
  alternateLinks: Array<{
    href: string;
    hrefLang: 'en' | 'x-default' | 'zh';
  }>;
  websiteStructuredData: string;
}

export interface PageHeadContext {
  locale: SupportedLocale;
  region: MessageRegion;
  siteUrl: string;
  ogImageUrl: string;
}

function getMessage(context: PageHeadContext, key: string, fallback = '') {
  return getMessagesForLocale(context.locale, context.region)[key] ?? fallback;
}

function buildAlternateLinks(context: PageHeadContext): PageHeadSpec['alternateLinks'] {
  const englishUrl = context.region === 'cn' ? 'https://creaticode.com' : context.siteUrl;
  const chineseUrl = context.region === 'cn' ? context.siteUrl : 'https://creaticode.cn';

  return [
    { href: englishUrl, hrefLang: 'en' },
    { href: chineseUrl, hrefLang: 'zh' },
    { href: englishUrl, hrefLang: 'x-default' },
  ];
}

function buildWebsiteStructuredData(context: PageHeadContext, siteName: string) {
  const alternateName = Array.from(
    new Set([siteName === 'CreatiCode' ? '' : 'CreatiCode', new URL(context.siteUrl).host.toLowerCase()].filter(Boolean)),
  );

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    alternateName,
    url: context.siteUrl,
  });
}

export function buildPageHead(context: PageHeadContext): PageHeadSpec {
  const description = getMessage(context, 'site.meta.description');
  const title = getMessage(context, 'site.title', 'CreatiCode');
  const ogTitle = getMessage(context, 'site.meta.og:title', title);
  const siteName = getMessage(context, 'TopBanner.creaticode', 'CreatiCode');

  return {
    title,
    description,
    keywords: getMessage(context, 'site.meta.keywords'),
    author: 'CreatiCode',
    canonicalUrl: context.siteUrl,
    siteName,
    ogTitle,
    ogDescription: description,
    ogUrl: context.siteUrl,
    ogImage: context.ogImageUrl,
    alternateLinks: buildAlternateLinks(context),
    websiteStructuredData: buildWebsiteStructuredData(context, siteName),
  };
}
